import { differenceInMonths, format, isValid, subMonths } from 'date-fns'
import { useMemo, useState } from 'react'
import { useCafSummaryOverviewV5 } from '../../../../../modules/apis/webview/caf'
import useOffsetDate from '../../../../../modules/hooks/webview/caf/common/useOffsetDate'
import { ai_analytics } from '../../../../../modules/i18n/strings/auto/wording'
import useTranslation from '../../../../../modules/i18n/useTranslation'
import { useLessonsStack } from '../../../../../pages/[lang]/webview/lessons'
import CafBanner from '../../../../banner/caf/CafBanner'
import LoadingSpinner from '../../../../common/LoadingSpinner'
import FilterSection from '../../../../new-portal/caf/statistic/FilterSection'
import LessonSection from '../../../../new-portal/caf/statistic/LessonSection'
import NoCAFPlaceholder from '../../../../new-portal/caf/statistic/NoCAFPlaceholder'
import PinnedHighlightSection from '../../../../new-portal/caf/statistic/PinnedHighlightSection'
import RecommHighlightSection from '../../../../new-portal/caf/statistic/RecommHighlightSection'
import TrendGraphSection from '../../../../new-portal/caf/statistic/TrendGraphSection'
import WebviewStackContainer from '../../../common/WebviewStackContainer'

const AIStatisticActivity = ({ params }) => {
  const { useLessonsFlow } = useLessonsStack()
  const { pop, push } = useLessonsFlow()
  const { initialStartDate, initialEndDate } = params
  const { t } = useTranslation()
  const today = new Date()
  const dateWOffset = useOffsetDate()
  const defaultStartDate = dateWOffset(subMonths(today, 1))
  const defaultEndDate = today

  const [startDate, setStartDate] = useState(
    isValid(new Date(initialStartDate)) ? new Date(initialStartDate) : defaultStartDate
  )
  const [endDate, setEndDate] = useState(isValid(new Date(initialEndDate)) ? new Date(initialEndDate) : defaultEndDate)
  const monthDiff = differenceInMonths(endDate, startDate)
  const [timePeriod, setTimePeriod] = useState<number>(monthDiff)

  const { data, isLoading, refetch } = useCafSummaryOverviewV5({
    startDate: format(startDate, 'yyyy-MM-dd'),
    endDate: format(endDate, 'yyyy-MM-dd'),
  })

  const noCAFData = useMemo(() => data?.count.forty === 0 && data?.count.twenty === 0, [data])

  return (
    <div>
      <WebviewStackContainer height="52px" onBack={pop} title={t(ai_analytics.title)} border={true}>
        {isLoading || !data ? (
          <LoadingSpinner show />
        ) : (
          <>
            <div className="mt-[20px] mb-[14px] px-[20px]">
              <FilterSection
                timePeriod={timePeriod}
                setTimePeriod={setTimePeriod}
                startDate={startDate}
                endDate={endDate}
                setStartDate={setStartDate}
                setEndDate={setEndDate}
              />
            </div>
            {noCAFData ? (
              <NoCAFPlaceholder />
            ) : (
              <div className="w-full px-[20px]">
                <LessonSection
                  start={format(startDate, 'yyyy-MM-dd')}
                  end={format(endDate, 'yyyy-MM-dd')}
                  count={data.count}
                />
                <div className="border-t-1 mb-[20px] w-full border-gray-200"></div>
                <TrendGraphSection trend={data.trend} />
                <CafBanner
                  pageType="statistic"
                  lessonBannerShow={data?.show_summary_popup}
                  lessonBannerType={data?.summary_popup_type}
                />
                <div className="border-t-1 w-full border-gray-200"></div>
                <RecommHighlightSection
                  startDate={format(startDate, 'yyyy-MM-dd')}
                  endDate={format(endDate, 'yyyy-MM-dd')}
                  count={data.count.twenty + data.count.forty}
                />
                <div className="border-t-1 w-full border-gray-200"></div>
                <PinnedHighlightSection
                  startDate={format(startDate, 'yyyy-MM-dd')}
                  endDate={format(endDate, 'yyyy-MM-dd')}
                />
                <div className="h-[60px]"></div>
              </div>
            )}
          </>
        )}
      </WebviewStackContainer>
    </div>
  )
}

export default AIStatisticActivity
